// src/components/common/ListPagination.tsx
/**
 * `CCMSPagination` with the spacing and the page-size options every list page wants.
 *
 * Two jobs beyond the raw component:
 *
 * 1. **Spacing.** `mt="xl"` and a `Divider`, so the controls read as a footer rather than another row.
 * 2. **Page-size options that match the layout**, and that always contain the current size.
 *
 * ⚠️ **Why point 2 matters.** `CCMSPagination` defaults to `[10, 20, 50, 100]` and renders the size as a
 * `Select` whose `value` is `String(pageSize)`. Several pages start at **12** (card grids), which is not
 * in that list, so the dropdown rendered with **nothing selected**. `ensureCurrent` below injects the
 * active size if it is missing, so the control can never come up blank no matter what a page passes.
 *
 * ⚠️ Imports `CCMSPagination` by **path, not through `@/components`**. This file is re-exported by that
 * barrel (`export * from './common'`), so importing the barrel here would be a cycle.
 */
import { Box, Divider } from '@/components/ui';
import CCMSPagination from '@/components/ccms-components/data-display/components/CCMSPagination';
import type { CCMSPaginationProps } from '@/components/ccms-components/data-display/types';

/**
 * Rows for tables and stacked lists.
 *
 * Grid sizes are multiples of **12**, so they divide evenly at **1, 2, 3 and 4** columns and leave no
 * half-empty row on a full page. Four matters: `CareSupportLeaders` and `facilitators` both use
 * `SimpleGrid cols={4}`, while `Administration/Users` is responsive 1/2/3. The row sizes would be ragged
 * in a grid (10 and 50 both leave 2 over at 4 columns), which is the whole reason for the split.
 *
 * The **last** page is still partial whenever the total is not a multiple of the page size. No choice of
 * page size can prevent that.
 */
export const ROW_PAGE_SIZES = [10, 20, 50, 100];
export const GRID_PAGE_SIZES = [12, 24, 48, 96];

export interface ListPaginationProps extends CCMSPaginationProps {
  /**
   * Which option set to offer. `grid` for card layouts, where the page size has to divide by the column
   * count. Ignored when `pageSizeOptions` is given explicitly.
   */
  layout?: 'rows' | 'grid';
}

/** Guarantees the active size is selectable, so the Select is never rendered with no match. */
const ensureCurrent = (options: number[], pageSize: number) =>
  (options.includes(pageSize) ? options : [...options, pageSize].sort((a, b) => a - b));

export function ListPagination({
  layout = 'rows',
  pageSizeOptions,
  ...rest
}: ListPaginationProps) {
  const base = pageSizeOptions ?? (layout === 'grid' ? GRID_PAGE_SIZES : ROW_PAGE_SIZES);

  return (
    <Box mt="xl">
      <Divider mb="md" />
      <CCMSPagination {...rest} pageSizeOptions={ensureCurrent(base, rest.pageSize)} />
    </Box>
  );
}

export default ListPagination;
